/**
 * Server-side (and offline-demo) replay: re-run a game from its seed and event
 * log with the same engine the client used.
 *
 * Replay semantics:
 * - The engine clock starts at 0 (clients must start their engine at 0 too).
 * - Events are applied in order: advance to `event.at`, then submitAnswer /
 *   endGame at `event.at`.
 * - A log the live engine could not have produced throws a ReplayError:
 *   out-of-order or malformed events, events after the run ended, an answer for
 *   a problem that isn't the falling block, or an answer the engine would ignore
 *   (after its block locked).
 * - Clients submit after game over. If the log ends while the run is still
 *   going (no `end` event, no top-out), the replay keeps advancing — every
 *   remaining block times out — until the stack tops out. That mirrors what the
 *   live game would have done had the player walked away, and makes a truncated
 *   log strictly worse than the real run (and usually longer than the session's
 *   wall clock, which validation rejects).
 */
import type { GameConfig, GameEvent, GameState, InputVia, Problem } from '../types.ts';
import { advance, createGame, endGame, submitAnswer } from './engine.ts';
import { nextTransitionAt } from './transitions.ts';

export interface ReplayInput {
  seed: number;
  startRating: number;
  referenceRating: number;
  events: readonly GameEvent[];
  config?: Partial<GameConfig>;
}

/** Replay failure categories — each maps onto an anti-cheat AnomalyCode of the same name. */
export type ReplayErrorCode = 'event-order' | 'unknown-problem' | 'answer-after-lock';

export class ReplayError extends Error {
  constructor(
    readonly code: ReplayErrorCode,
    /** Index of the offending event in the log. */
    readonly eventIndex: number,
    message: string,
  ) {
    super(`event #${eventIndex}: ${message}`);
    this.name = 'ReplayError';
  }
}

export interface ReplayTrace {
  /** Final state (game over). */
  state: GameState;
  /** Every problem the player answered, by problem id (for prompt-based timing checks). */
  answered: ReadonlyMap<string, Problem>;
}

const VIA: ReadonlySet<string> = new Set<InputVia>(['mc', 'hw', 'kb']);

/** Upper bound on run-out steps after the last event; each lock adds a brick, so a handful suffice. */
const MAX_RUN_OUT_STEPS = 10_000;

/** Re-run a game from its seed and event log. Throws ReplayError on malformed/out-of-order events. */
export function replayRun(input: ReplayInput): GameState {
  return replayRunTrace(input).state;
}

/** replayRun, also returning the answered problems (used by validateRun). */
export function replayRunTrace(input: ReplayInput): ReplayTrace {
  let state = createGame({
    seed: input.seed,
    startRating: input.startRating,
    referenceRating: input.referenceRating,
    now: 0,
    ...(input.config ? { config: input.config } : {}),
  });
  const answered = new Map<string, Problem>();
  let lastAt = 0;

  for (const [index, event] of input.events.entries()) {
    const fail = (code: ReplayErrorCode, message: string): never => {
      throw new ReplayError(code, index, message);
    };
    if (!isWellFormed(event)) fail('event-order', 'malformed event');
    if (event.at < lastAt) fail('event-order', `at ${event.at} is earlier than the previous event (${lastAt})`);
    lastAt = event.at;

    const current = advance(state, event.at);
    if (current.phase === 'gameOver') fail('event-order', `${event.type} event after the run ended`);

    if (event.type === 'end') {
      state = endGame(current, event.at);
      continue;
    }

    const block = current.active;
    if (current.phase !== 'falling' || !block || block.problem.id !== event.problemId) {
      const resolved = current.history.find((r) => r.problemId === event.problemId);
      if (!resolved) fail('unknown-problem', `answer for problem "${event.problemId}", which is not the falling block`);
      else if (resolved.outcome === 'timeout') fail('answer-after-lock', `answer for problem "${event.problemId}" after it locked`);
      else fail('event-order', `second answer for problem "${event.problemId}"`);
    }
    const after = submitAnswer(current, { value: event.value, via: event.via }, event.at);
    if (after === current) fail('answer-after-lock', `answer for problem "${event.problemId}" was not accepted`);
    if (after.lastResolution) answered.set(event.problemId, after.lastResolution.problem);
    state = after;
  }

  return { state: runOut(state), answered };
}

/** Advance transition by transition (timeouts, phase ends) until the run is over. */
function runOut(state: GameState): GameState {
  let s = state;
  for (let i = 0; i < MAX_RUN_OUT_STEPS && s.phase !== 'gameOver'; i++) s = advance(s, nextTransitionAt(s));
  if (s.phase !== 'gameOver') throw new Error('replayRun: run did not end while simulating timeouts');
  return s;
}

function isWellFormed(event: unknown): event is GameEvent {
  if (typeof event !== 'object' || event === null) return false;
  const e = event as Record<string, unknown>;
  if (typeof e.at !== 'number' || !Number.isFinite(e.at) || e.at < 0) return false;
  if (e.type === 'end') return true;
  return e.type === 'answer' && typeof e.problemId === 'string' && typeof e.value === 'string' && typeof e.via === 'string' && VIA.has(e.via);
}
